import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, CircularProgress } from '@mui/material';
import { LogoMark } from '../Common/SiteLogo';

const loadingMessages = [
  "✨ Getting things ready...",
  "🚀 Hang in there...",
  "🎨 Loading things for you...",
  "🤖 AI is working its magic...",
  "⚡ Crafting your perfect resume...",
  "💼 Analyzing your experience...",
  "📝 Generating professional content...",
  "🎯 Tailoring to your job description...",
  "✍️ Writing compelling bullet points...",
  "🌟 Making you stand out...",
  "💡 Adding the perfect touches...",
  "🔥 Creating something amazing...",
  "⏳ Almost there...",
  "🎪 Preparing your showcase...",
  "🏆 Building your success story...",
  "💪 Highlighting your strengths...",
  "🎓 Showcasing your expertise...",
  "🌈 Adding some polish...",
  "⚙️ Fine-tuning the details...",
  "🎉 Getting ready to impress..."
];

const LoadingOverlay = ({ message, progress: propProgress }) => {
  const [currentMessage, setCurrentMessage] = useState(loadingMessages[0]);
  const [localProgress, setLocalProgress] = useState(0);

  // Use provided progress or simulate it
  const displayProgress = propProgress !== undefined ? propProgress : localProgress;

  useEffect(() => {
    // Only rotate messages if no message is provided
    if (!message) {
      let msgIdx = 0;
      const messageInterval = setInterval(() => {
        msgIdx = (msgIdx + 1) % loadingMessages.length;
        setCurrentMessage(loadingMessages[msgIdx]);
      }, 2000);

      return () => clearInterval(messageInterval);
    }
  }, [message]);

  useEffect(() => {
    // Only simulate progress if no progress is provided
    if (propProgress === undefined) {
      const progressInterval = setInterval(() => {
        setLocalProgress((prev) => {
          if (prev >= 95) return 95; // Stop at 95% until actual completion
          return prev + Math.random() * 10;
        });
      }, 500);

      return () => clearInterval(progressInterval);
    }
  }, [propProgress]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        animation: 'fadeIn 0.3s ease-in'
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: 500,
          px: 4,
          animation: 'slideUp 0.5s ease-out'
        }}
      >
        {/* Animated Icon */}
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            mb: 4
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                opacity: 0.3,
                filter: 'blur(20px)'
              }}
            />
          </Box>
          
          <CircularProgress
            size={100}
            thickness={2}
            sx={{
              color: 'white',
              position: 'relative',
              zIndex: 1
            }}
          />
          
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: 'rotate 3s linear infinite'
            }}
          >
            <LogoMark size={40} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>

        {/* Loading Message */}
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            fontWeight: 600,
            mb: 2,
            animation: 'fadeInOut 2s ease-in-out infinite'
          }}
        >
          {message || currentMessage}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            mb: 4
          }}
        >
          This usually takes 10-15 seconds
        </Typography>

        {/* Progress Bar */}
        <Box sx={{ width: '100%', mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={displayProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                animation: 'shimmer 2s infinite'
              }
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)'
          }}
        >
          {Math.round(displayProgress)}% complete
        </Typography>

        {/* Fun Facts */}
        <Box
          sx={{
            mt: 4,
            p: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <LogoMark size={28} sx={{ mx: 'auto', mb: 1, opacity: 0.9 }} />
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              display: 'block'
            }}
          >
            💡 Tip: AI-generated resumes increase interview chances by 40%
          </Typography>
        </Box>
      </Box>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.3;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.1);
              opacity: 0.5;
            }
          }
          
          @keyframes rotate {
            from {
              transform: translate(-50%, -50%) rotate(0deg);
            }
            to {
              transform: translate(-50%, -50%) rotate(360deg);
            }
          }
          
          @keyframes fadeInOut {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          @keyframes shimmer {
            0% {
              background-position: -1000px 0;
            }
            100% {
              background-position: 1000px 0;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default LoadingOverlay;

